import React, { useCallback } from 'react';
import P from 'prop-types';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';
import { Box, Title, Text } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';
import IframeResizer from 'iframe-resizer-react';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';
import getTextColor from '../../lib/text-color-config';
import parseMarkdownLinks from '../../lib/parse-md-links';
import styles from './third-party-embed.module.less';
import BackgroundColor from '../background-color/background-color';

const getTextColorStyle = (textColorOptions, propertyName = 'color') => {
  const { textColor: color } = getTextColor(textColorOptions);
  return { [propertyName]: color };
};

const linkParser = (content) => parseMarkdownLinks(content, false);

const isValidUrl = (url) => !!url.match(/^(http|https):\/\//);

const htmlOutputParser = (content, enableHtml = false) => {
  if (enableHtml) {
    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return linkParser(content);
};

const scriptEmbedContent = (scriptSrc) => {
  const htmlSnippet = `
    <script
      src="${scriptSrc}">
    </script>
  `;
  if (!scriptSrc) {
    return null;
  }
  return (
    <span
      id="script-embed-container"
      style={{ textAlign: 'left' }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: htmlSnippet }}
    />
  );
};

const iframeEmbedContent = (iframeSrc, height, width) => {
  return (
    isValidUrl(iframeSrc) && (
      <Box id="iframe-wrapper" className={styles.iframeBox}>
        <IframeResizer
          src={iframeSrc}
          style={{ minWidth: width || '100%', maxHeight: height || '100%' }}
        />
      </Box>
    )
  );
};

const renderContent = (embedType, iframeSrc, scriptSrc, height, width) => {
  const type = {
    iframe: iframeEmbedContent(iframeSrc, height, width),
    script: scriptEmbedContent(scriptSrc),
  };

  if (
    (embedType === 'script' && !scriptSrc) ||
    (embedType === 'iframe' && !iframeSrc)
  ) {
    return (
      <Box id="default-empty-src-message" style={{ margin: '2rem 0' }}>
        <Text size="large" bold>
          Enter a {embedType} URL to make the content appear
        </Text>
      </Box>
    );
  }

  return type[embedType];
};

const getAlignment = (alignment) => {
  const type = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
  };
  return type[alignment];
};

const ThirdPartyEmbed = ({
  ThirdPartyEmbedImageDesktop,
  ThirdPartyEmbedImageMobile,
  ThirdPartyEmbedImageTablet,
  src,
  scriptSrc,
  embedType,
  height,
  width,
  cardIcon,
  cardTitle,
  cardTitleChronicle,
  enableHtmlInCardTitle,
  cardTitleColorOptions,
  cardTitleSize,
  cardSubtitle,
  cardSubtitleColorOptions,
  cardSubtitleSize,
  enableHtmlInCardSubtitle,
  size,
  alignment,
  topFormMargin,
  bottomFormMargin,
  topFormPadding,
  bottomFormPadding,
  bgOptions,
  disableTopBottomCardColor,
}) => {
  const getBackgroundImageSrcMap = useCallback(
    (bgSize) => {
      const type = {
        lg: ThirdPartyEmbedImageDesktop,
        md: ThirdPartyEmbedImageTablet,
        sm: ThirdPartyEmbedImageMobile,
      };
      if (bgSize !== 'lg' && !type[bgSize]) {
        return type.lg;
      }
      return type[bgSize];
    },
    [
      ThirdPartyEmbedImageDesktop,
      ThirdPartyEmbedImageMobile,
      ThirdPartyEmbedImageTablet,
    ]
  );

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <Box
          className={styles.container}
          style={{
            backgroundColor,
            backgroundImage: `url(${getBackgroundImageSrcMap(size)})`,
            alignItems: getAlignment(alignment),
          }}
        >
          <Box
            className={styles.card}
            style={{
              ...(disableTopBottomCardColor && { backgroundColor }),
              marginTop: topFormMargin ?? 85,
              marginBottom: bottomFormMargin ?? 63,
              paddingTop: topFormPadding ?? 50,
              paddingBottom: bottomFormPadding ?? 50,
            }}
          >
            {cardIcon && (
              <Image
                style={{ maxHeight: 90 }}
                src={cardIcon}
                alt="NerdWallet Brand"
                backgroundColor="transparent"
                naturalHeight={90}
                naturalWidth={221}
              />
            )}
            <Box className={styles.cardHeading}>
              <Title
                style={getTextColorStyle(cardTitleColorOptions)}
                className={classNames(styles.cardTitle, [
                  { [styles.titleChronicle]: cardTitleChronicle },
                ])}
                size={cardTitleSize}
              >
                {htmlOutputParser(cardTitle, enableHtmlInCardTitle)}
              </Title>
              <Box
                style={getTextColorStyle(cardSubtitleColorOptions)}
                className={styles.cardSubtitle}
              >
                <Text size={cardSubtitleSize} component="p">
                  {htmlOutputParser(cardSubtitle, enableHtmlInCardSubtitle)}
                </Text>
              </Box>
            </Box>
            {renderContent(embedType, src, scriptSrc, height, width)}
          </Box>
        </Box>
      )}
    </BackgroundColor>
  );
};

ThirdPartyEmbed.propTypes = {
  cardTitle: P.string,
  cardTitleSize: P.oneOf(['default', 'small']),
  cardSubtitle: P.string,
  cardSubtitleSize: P.oneOf(['default', 'large', 'small', 'x-small']),
  embedType: P.oneOf(['script', 'iframe']),
  alignment: P.oneOf(['left', 'center', 'right']),
  cardIcon: P.string,
  ThirdPartyEmbedImageDesktop: P.string,
  ThirdPartyEmbedImageMobile: P.string,
  ThirdPartyEmbedImageTablet: P.string,
  topFormMargin: P.number,
  bottomFormMargin: P.number,
  topFormPadding: P.number,
  bottomFormPadding: P.number,
  size: P.string,
  cardTitleChronicle: P.bool,
  enableHtmlInCardTitle: P.bool,
  enableHtmlInCardSubtitle: P.bool,
  bgOptions: P.shape({
    backgroundColor: P.string,
  }),
  cardTitleColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  cardSubtitleColorOptions: P.shape({
    hexColor: P.string,
    textColor: P.string,
  }),
  children: P.node,
  src: P.string,
  scriptSrc: P.string,
  formTitle: P.string,
  height: P.string,
  width: P.string,
  disableTopBottomCardColor: P.bool,
};

ThirdPartyEmbed.defaultProps = {
  src: '',
  scriptSrc: '',
  formTitle: '',
  embedType: 'iframe',
  height: '100%',
  width: '100%',
  bgOptions: BackgroundColor.defaultProps.bgOptions,
  disableTopBottomCardColor: false,
};

ThirdPartyEmbed.getEditingConfig = (componentDocument) => {
  const embedTypeInputConfig =
    componentDocument?.props?.embedType === 'script'
      ? [stringTextAreaSpec('scriptSrc', 'Script Source URL')]
      : [
          stringInputSpec('src', 'Iframe Source URL'),
          stringInputSpec('height', 'Iframe Height', {
            help: 'Defaults to "100%"',
          }),
          stringInputSpec('width', 'Iframe Width', {
            help: 'Defaults to "100%"',
          }),
        ];
  return [
    group('Title', [
      stringInputSpec('cardTitle', 'Title'),
      stringSelectSpec(
        'cardTitleSize',
        'Title Size',
        [
          { label: 'Small', value: 'small' },
          { label: 'Large', value: 'default' },
        ],
        {
          help: 'Defaults to large',
        }
      ),
      boolCheckboxSpec('cardTitleChronicle', 'Title in Chronicle?'),
      boolCheckboxSpec('enableHtmlInCardTitle', 'Allow HTML in Title?', {
        help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
      }),
      ...getTextColor.getEditingConfig(
        componentDocument,
        'cardTitleColorOptions',
        'Title Color'
      ),
    ]),
    group('Subtitle', [
      stringInputSpec('cardSubtitle', 'Subtitle'),
      stringSelectSpec(
        `cardSubtitleSize`,
        'Subtitle Size',
        [
          { label: 'Default', value: 'default' },
          { label: 'Large', value: 'large' },
          { label: 'Small', value: 'small' },
          { label: 'X-Small', value: 'x-small' },
        ],
        {
          help: 'Defaults to small',
        }
      ),
      ...getTextColor.getEditingConfig(
        componentDocument,
        'cardSubtitleColorOptions',
        'Subtitle Color'
      ),
      boolCheckboxSpec('enableHtmlInCardSubtitle', 'Allow HTML in Subtitle?', {
        help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
      }),
    ]),
    group('Third Party Embed', [
      stringSelectSpec(
        `embedType`,
        'Embed Type',
        [
          { label: 'Iframe', value: 'iframe' },
          { label: 'Script', value: 'script' },
        ],
        {
          help: 'Defaults to "Iframe"',
        }
      ),
      stringSelectSpec(
        `alignment`,
        'Content Alignment',
        [
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ],
        {
          help: 'Defaults to "Center"',
        }
      ),
      ...embedTypeInputConfig,
    ]),
    group('Background', [
      stringInputSpec('cardIcon', 'Card Icon'),
      ...BackgroundColor.getEditingConfig(componentDocument),
      stringInputSpec(
        'ThirdPartyEmbedImageDesktop',
        'Background Image URL (Desktop)'
      ),
      stringInputSpec(
        'ThirdPartyEmbedImageTablet',
        'Background Image URL (Tablet)'
      ),
      stringInputSpec(
        'ThirdPartyEmbedImageMobile',
        'Background Image URL (Mobile)'
      ),
      numberInputSpec('topFormMargin', 'Top Form Margin'),
      numberInputSpec('bottomFormMargin', 'Bottom Form Margin'),
      numberInputSpec('topFormPadding', 'Top Form Padding'),
      numberInputSpec('bottomFormPadding', 'Bottom Form Padding'),
      boolCheckboxSpec(
        'disableTopBottomCardColor',
        'Disable Top/Bottom Card Color'
      ),
    ]),
  ];
};

ThirdPartyEmbed.displayName = 'ThirdPartyEmbed';
export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(ThirdPartyEmbed);
